<template>
  <div class="api-quota-overview" v-loading="loading">
    <el-alert
      v-if="serviceHint"
      class="service-hint"
      :title="serviceHint"
      type="info"
      :closable="false"
      show-icon
    />

    <el-row :gutter="16" class="stats-row">
      <el-col :xs="12" :sm="6">
        <div class="stat-card">
          <div class="stat-value">{{ summary.total }}</div>
          <div class="stat-label">已巡检账号</div>
        </div>
      </el-col>
      <el-col :xs="12" :sm="6">
        <div class="stat-card is-ok">
          <div class="stat-value">{{ summary.ok }}</div>
          <div class="stat-label">正常 / 可查</div>
        </div>
      </el-col>
      <el-col :xs="12" :sm="6">
        <div class="stat-card is-low">
          <div class="stat-value">{{ summary.low }}</div>
          <div class="stat-label">余额不足</div>
        </div>
      </el-col>
      <el-col :xs="12" :sm="6">
        <div class="stat-card is-error">
          <div class="stat-value">{{ summary.error + summary.missing }}</div>
          <div class="stat-label">异常 / 未配置</div>
        </div>
      </el-col>
    </el-row>

    <div class="toolbar">
      <el-button type="primary" :loading="loading" @click="loadData">刷新额度</el-button>
    </div>

    <el-table :data="items" border stripe empty-text="暂无模型账号数据">
      <el-table-column prop="display_name" label="账号 / 厂商" min-width="200">
        <template #default="{ row }">
          <div class="name-cell">
            <strong>{{ row.display_name }}</strong>
            <span class="provider-code">{{ row.provider }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="models" label="模型" min-width="200">
        <template #default="{ row }">
          <div class="models-cell">
            <el-tag
              v-for="model in (row.models || []).filter(Boolean)"
              :key="model"
              size="small"
              class="model-tag"
              effect="plain"
            >{{ model }}</el-tag>
            <span v-if="!(row.models || []).filter(Boolean).length">—</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="balance_text" label="余额" width="140" />
      <el-table-column prop="usage_text" label="用量说明" min-width="160" show-overflow-tooltip />
      <el-table-column prop="status_label" label="状态" width="120">
        <template #default="{ row }">
          <el-tag size="small" :type="statusTagType(row.status)">{{ row.status_label }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="api_key_masked" label="Key" width="130" show-overflow-tooltip>
        <template #default="{ row }">{{ row.api_key_masked || '—' }}</template>
      </el-table-column>
      <el-table-column prop="message" label="备注" min-width="220" show-overflow-tooltip />
      <el-table-column label="操作" width="100" fixed="right">
        <template #default="{ row }">
          <el-button
            v-if="row.console_url"
            type="primary"
            link
            @click="openConsole(row.console_url)"
          >控制台</el-button>
          <span v-else>—</span>
        </template>
      </el-table-column>
    </el-table>

    <ul v-if="notes.length" class="notes">
      <li v-for="(note, idx) in notes" :key="idx">{{ note }}</li>
    </ul>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { getLlmQuotaSnapshot } from '@/api/apiQuota'

const loading = ref(false)
const items = ref([])
const notes = ref([])
const serviceHint = ref('')

const summary = reactive({
  total: 0,
  ok: 0,
  low: 0,
  error: 0,
  missing: 0,
})

function statusTagType(status) {
  if (status === 'ok' || status === 'local') return 'success'
  if (status === 'manual' || status === 'configured') return 'warning'
  if (status === 'low' || status === 'error' || status === 'missing') return 'danger'
  return 'info'
}

function openConsole(url) {
  if (!url) return
  window.open(url, '_blank', 'noopener,noreferrer')
}

async function loadData() {
  loading.value = true
  try {
    const res = await getLlmQuotaSnapshot()
    const payload = res.data || {}
    items.value = payload.items || []
    notes.value = payload.notes || []
    const s = payload.summary || {}
    summary.total = Number(s.total || 0)
    summary.ok = Number(s.ok || 0)
    summary.low = Number(s.low || 0)
    summary.error = Number(s.error || 0)
    summary.missing = Number(s.missing || 0)
    serviceHint.value = ''
  } catch (error) {
    const message = error?.response?.data?.error?.message
      || error?.response?.data?.detail
      || error?.message
      || '加载 API 额度失败'
    serviceHint.value = '请确认 Java 后台与 Python AI 服务均已启动，且 bymy.ai-service-base-url 指向正确地址。'
    ElMessage.error(message)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadData()
})
</script>

<style scoped>
.api-quota-overview {
  min-height: 240px;
}

.service-hint {
  margin-bottom: 16px;
}

.stats-row {
  margin-bottom: 16px;
}

.stat-card {
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 12px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  line-height: 1.2;
}

.stat-label {
  margin-top: 6px;
  font-size: 13px;
  color: #666;
}

.stat-card.is-ok .stat-value { color: #27c24c; }
.stat-card.is-low .stat-value { color: #e6a23c; }
.stat-card.is-error .stat-value { color: #f56c6c; }

.toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.name-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.provider-code {
  font-size: 12px;
  color: #999;
}

.models-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.model-tag {
  max-width: 100%;
}

.notes {
  margin: 16px 0 0;
  padding-left: 18px;
  color: #888;
  font-size: 12px;
  line-height: 1.7;
}
</style>
